/**
 * Annotation-Editor (F5 + Schwärzen, PRD §8 Datenschutz).
 *
 * Werkzeuge: Zuschneiden, Rechteck, Kreis, Pfeil, Buchstabe, Schwärzen, Lupe.
 * - Zuschneiden: Rahmen aufziehen → das Bild wird auf den Bereich beschnitten.
 * - Rechteck/Kreis/Pfeil/Buchstaben werden beim Übernehmen ROT ins PNG
 *   eingebrannt, damit die Erfassungs-KI die Markierungen sieht („der
 *   markierte Bereich"); zusätzlich bleiben sie als Vektor-Daten an der Meldung.
 * - Buchstabe: Klick setzt einen Marker (A, B, C, …) — zum Referenzieren
 *   mehrerer Stellen im Dialog.
 * - Schwärzungen werden deckend eingebrannt und NICHT als Vektor mitgeschickt —
 *   das Original verlässt den Browser nie ungeschwärzt.
 * - Lupe: vergrößert den Bereich unter dem Zeiger (verändert das Bild nicht).
 */

import { useCallback, useEffect, useRef, useState } from "react"
import type { Annotation } from "@/types"

type Werkzeug =
  | "zuschneiden"
  | "rechteck"
  | "kreis"
  | "pfeil"
  | "buchstabe"
  | "zahl"
  | "schwaerzen"
  | "lupe"

const MAX_ZAHL = 10 // Zahlen-Stempel: 1 bis 10

const LUPE_D = 180 // Durchmesser der Lupe (px, Bildschirm)
const LUPE_ZOOM = 2.5 // Vergrößerung relativ zur Anzeige

interface Schwaerzung {
  x: number
  y: number
  w: number
  h: number
}

interface Props {
  bild: Blob
  onFertig: (ergebnis: { bild: Blob; annotationen: Annotation[] }) => void
  onAbbrechen: () => void
}

interface Form {
  art: Werkzeug
  x: number
  y: number
  w: number
  h: number
  zeichen?: string
}

function pfeilZeichnen(ctx: CanvasRenderingContext2D, f: Form) {
  // Pfeil von (x,y) nach (x+w, y+h)
  const vonX = f.x
  const vonY = f.y
  const nachX = f.x + f.w
  const nachY = f.y + f.h
  const winkel = Math.atan2(nachY - vonY, nachX - vonX)
  const laenge = Math.hypot(f.w, f.h)
  const kopf = Math.min(18, laenge / 3)

  ctx.beginPath()
  ctx.moveTo(vonX, vonY)
  ctx.lineTo(nachX, nachY)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(nachX, nachY)
  ctx.lineTo(
    nachX - kopf * Math.cos(winkel - Math.PI / 6),
    nachY - kopf * Math.sin(winkel - Math.PI / 6)
  )
  ctx.moveTo(nachX, nachY)
  ctx.lineTo(
    nachX - kopf * Math.cos(winkel + Math.PI / 6),
    nachY - kopf * Math.sin(winkel + Math.PI / 6)
  )
  ctx.stroke()
}

function markerZeichnen(ctx: CanvasRenderingContext2D, f: Form, farbe: string) {
  // Marker-Kreis mit Zeichen — Größe skaliert mit dem Bild, damit er
  // auf großen Screenshots nicht untergeht; zweistellige Zeichen ("10")
  // bekommen eine etwas kleinere Schrift
  const r = Math.max(16, Math.round(ctx.canvas.width * 0.016))
  const zeichen = f.zeichen ?? "?"
  ctx.save()
  ctx.fillStyle = farbe
  ctx.beginPath()
  ctx.arc(f.x, f.y, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = "#ffffff"
  const groesse = Math.round(r * (zeichen.length > 1 ? 0.95 : 1.25))
  ctx.font = `bold ${groesse}px Inter, sans-serif`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(zeichen, f.x, f.y + r * 0.05)
  ctx.restore()
}

function formZeichnen(ctx: CanvasRenderingContext2D, f: Form) {
  if (f.art === "schwaerzen") {
    ctx.fillStyle = "rgba(0, 0, 0, 0.85)"
    ctx.fillRect(f.x, f.y, f.w, f.h)
    return
  }
  if (f.art === "zuschneiden") {
    // Vorschau des Zuschnitt-Rahmens (wird nie eingebrannt)
    ctx.save()
    ctx.strokeStyle = "#2563eb"
    ctx.lineWidth = 2
    ctx.setLineDash([8, 6])
    ctx.strokeRect(f.x, f.y, f.w, f.h)
    ctx.restore()
    return
  }
  if (f.art === "buchstabe") {
    markerZeichnen(ctx, f, "#dc2626")
    return
  }
  if (f.art === "zahl") {
    markerZeichnen(ctx, f, "#2563eb")
    return
  }
  ctx.strokeStyle = "#dc2626"
  ctx.lineWidth = 3
  if (f.art === "rechteck") {
    ctx.strokeRect(f.x, f.y, f.w, f.h)
  } else if (f.art === "kreis") {
    // Ellipse in der aufgezogenen Box
    const r = normalisiert(f)
    ctx.beginPath()
    ctx.ellipse(r.x + r.w / 2, r.y + r.h / 2, r.w / 2, r.h / 2, 0, 0, Math.PI * 2)
    ctx.stroke()
  } else {
    pfeilZeichnen(ctx, f)
  }
}

/** Negative Breite/Höhe normalisieren (Aufziehen in beliebige Richtung) */
function normalisiert(f: Form): { x: number; y: number; w: number; h: number } {
  return {
    x: Math.min(f.x, f.x + f.w),
    y: Math.min(f.y, f.y + f.h),
    w: Math.abs(f.w),
    h: Math.abs(f.h),
  }
}

export function AnnotationEditor({ bild, onFertig, onAbbrechen }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const lupeRef = useRef<HTMLCanvasElement>(null)
  // Quelle als Canvas (statt Image), damit Zuschneiden das Bild ersetzen kann
  const quelleRef = useRef<HTMLCanvasElement | null>(null)
  // Start mit Zuschneiden: erst den relevanten Ausschnitt wählen, dann markieren
  const [werkzeug, setWerkzeug] = useState<Werkzeug>("zuschneiden")
  const [formen, setFormen] = useState<Form[]>([])
  const [aktuelle, setAktuelle] = useState<Form | null>(null)
  const [lupe, setLupe] = useState<{ cx: number; cy: number; ix: number; iy: number } | null>(null)

  // Bild laden und Canvas dimensionieren
  useEffect(() => {
    const url = URL.createObjectURL(bild)
    const img = new Image()
    img.onload = () => {
      const quelle = document.createElement("canvas")
      quelle.width = img.naturalWidth
      quelle.height = img.naturalHeight
      quelle.getContext("2d")!.drawImage(img, 0, 0)
      quelleRef.current = quelle
      const canvas = canvasRef.current
      if (canvas) {
        canvas.width = quelle.width
        canvas.height = quelle.height
      }
      neuZeichnen()
    }
    img.src = url
    return () => URL.revokeObjectURL(url)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bild])

  const neuZeichnen = useCallback(
    (vorschau?: Form | null) => {
      const canvas = canvasRef.current
      const quelle = quelleRef.current
      const ctx = canvas?.getContext("2d")
      if (!canvas || !quelle || !ctx) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(quelle, 0, 0)
      for (const f of formen) formZeichnen(ctx, f)
      if (vorschau) formZeichnen(ctx, vorschau)
    },
    [formen]
  )

  /** Zuschneiden: Quelle auf den Rahmen beschneiden, Formen mitverschieben */
  const zuschneiden = (f: Form) => {
    const quelle = quelleRef.current
    const canvas = canvasRef.current
    if (!quelle || !canvas) return
    const r = normalisiert(f)
    // Auf Bildgrenzen begrenzen
    const x = Math.max(0, Math.round(r.x))
    const y = Math.max(0, Math.round(r.y))
    const w = Math.min(quelle.width - x, Math.round(r.w))
    const h = Math.min(quelle.height - y, Math.round(r.h))
    if (w < 20 || h < 20) return // zu kleiner Rahmen: ignorieren

    const neu = document.createElement("canvas")
    neu.width = w
    neu.height = h
    neu.getContext("2d")!.drawImage(quelle, x, y, w, h, 0, 0, w, h)
    quelleRef.current = neu
    canvas.width = w
    canvas.height = h
    setFormen((alte) => alte.map((form) => ({ ...form, x: form.x - x, y: form.y - y })))
  }

  useEffect(() => {
    neuZeichnen(aktuelle)
  }, [formen, aktuelle, neuZeichnen])

  // Lupe nachzeichnen: vergrößert den Bereich um den Zeiger aus dem
  // Anzeige-Canvas (inkl. Markierungen)
  useEffect(() => {
    const canvas = canvasRef.current
    const lc = lupeRef.current
    if (!canvas || !lc || !lupe) return
    const ctx = lc.getContext("2d")!
    const rect = canvas.getBoundingClientRect()
    const anzeigeSkala = rect.width / canvas.width
    const quellSeite = LUPE_D / (LUPE_ZOOM * anzeigeSkala)
    ctx.imageSmoothingEnabled = true
    ctx.clearRect(0, 0, LUPE_D, LUPE_D)
    ctx.drawImage(
      canvas,
      lupe.ix - quellSeite / 2,
      lupe.iy - quellSeite / 2,
      quellSeite,
      quellSeite,
      0,
      0,
      LUPE_D,
      LUPE_D
    )
    // Fadenkreuz
    ctx.strokeStyle = "rgba(220, 38, 38, 0.6)"
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(LUPE_D / 2, 0)
    ctx.lineTo(LUPE_D / 2, LUPE_D)
    ctx.moveTo(0, LUPE_D / 2)
    ctx.lineTo(LUPE_D, LUPE_D / 2)
    ctx.stroke()
  }, [lupe])

  /** Maus-Position in Bild-Pixel-Koordinaten (Canvas ist per CSS skaliert) */
  const bildKoordinaten = (e: React.MouseEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    }
  }

  /** Nächster freier Buchstabe (A, B, C, … — nach Z wieder A) */
  const naechsterBuchstabe = () =>
    String.fromCharCode(65 + (formen.filter((f) => f.art === "buchstabe").length % 26))

  /** Nächste freie Zahl (1–10; null wenn ausgeschöpft) */
  const naechsteZahl = () => {
    const n = formen.filter((f) => f.art === "zahl").length + 1
    return n <= MAX_ZAHL ? n : null
  }

  const onMouseDown = (e: React.MouseEvent) => {
    if (werkzeug === "lupe") return
    const p = bildKoordinaten(e)
    if (werkzeug === "buchstabe") {
      // Klick setzt den Marker direkt (kein Aufziehen)
      setFormen([...formen, { art: "buchstabe", x: p.x, y: p.y, w: 0, h: 0, zeichen: naechsterBuchstabe() }])
      return
    }
    if (werkzeug === "zahl") {
      const n = naechsteZahl()
      if (n === null) return // alle 10 vergeben
      setFormen([...formen, { art: "zahl", x: p.x, y: p.y, w: 0, h: 0, zeichen: String(n) }])
      return
    }
    setAktuelle({ art: werkzeug, x: p.x, y: p.y, w: 0, h: 0 })
  }
  const onMouseMove = (e: React.MouseEvent) => {
    if (werkzeug === "lupe") {
      const p = bildKoordinaten(e)
      setLupe({ cx: e.clientX, cy: e.clientY, ix: p.x, iy: p.y })
      return
    }
    if (!aktuelle) return
    const p = bildKoordinaten(e)
    setAktuelle({ ...aktuelle, w: p.x - aktuelle.x, h: p.y - aktuelle.y })
  }
  const onMouseUp = () => {
    if (!aktuelle) return
    if (aktuelle.art === "zuschneiden") {
      zuschneiden(aktuelle)
    } else if (Math.abs(aktuelle.w) > 4 || Math.abs(aktuelle.h) > 4) {
      setFormen([...formen, aktuelle])
    }
    setAktuelle(null)
  }

  const uebernehmen = async () => {
    const quelle = quelleRef.current
    if (!quelle) return
    // Export: Schwärzungen deckend + Markierungen ROT einbrennen —
    // die Erfassungs-KI bekommt dieses PNG und muss die Markierungen sehen
    const exportCanvas = document.createElement("canvas")
    exportCanvas.width = quelle.width
    exportCanvas.height = quelle.height
    const ctx = exportCanvas.getContext("2d")!
    ctx.drawImage(quelle, 0, 0)
    for (const f of formen) {
      if (f.art === "schwaerzen") {
        const s: Schwaerzung = normalisiert(f)
        ctx.fillStyle = "#000000"
        ctx.fillRect(s.x, s.y, s.w, s.h)
      } else {
        formZeichnen(ctx, f)
      }
    }
    const exportBlob = await new Promise<Blob>((resolve, reject) => {
      exportCanvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Export fehlgeschlagen"))),
        "image/png"
      )
    })
    const annotationen: Annotation[] = formen
      .filter(
        (f): f is Form & { art: "rechteck" | "pfeil" | "kreis" | "buchstabe" | "zahl" } =>
          f.art === "rechteck" ||
          f.art === "pfeil" ||
          f.art === "kreis" ||
          f.art === "buchstabe" ||
          f.art === "zahl"
      )
      .map((f) => ({ art: f.art, x: f.x, y: f.y, w: f.w, h: f.h, ...(f.zeichen ? { zeichen: f.zeichen } : {}) }))
    onFertig({ bild: exportBlob, annotationen })
  }

  const werkzeugKnopf = (w: Werkzeug, label: string) => (
    <button
      type="button"
      onClick={() => {
        setWerkzeug(w)
        if (w !== "lupe") setLupe(null)
      }}
      className={`rounded-lg px-3.5 py-2 text-[13px] font-semibold transition-colors ${
        werkzeug === w ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80"
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/55 p-4">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-3 overflow-hidden rounded-[14px] bg-card p-5 shadow-2xl">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-2 text-sm font-bold">Screenshot bearbeiten</span>
          {werkzeugKnopf("zuschneiden", "Zuschneiden")}
          {werkzeugKnopf("rechteck", "Rahmen")}
          {werkzeugKnopf("kreis", "Kreis")}
          {werkzeugKnopf("pfeil", "Pfeil")}
          {werkzeugKnopf("buchstabe", `Buchstabe (${naechsterBuchstabe()})`)}
          {werkzeugKnopf("zahl", naechsteZahl() !== null ? `Zahl (${naechsteZahl()})` : "Zahl (voll)")}
          {werkzeugKnopf("schwaerzen", "Schwärzen")}
          {werkzeugKnopf("lupe", "Lupe")}
          <button
            type="button"
            onClick={() => setFormen(formen.slice(0, -1))}
            disabled={formen.length === 0}
            className="rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold disabled:opacity-40"
          >
            Rückgängig
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onAbbrechen}
            className="rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void uebernehmen()}
            className="rounded-lg bg-primary px-3.5 py-2 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover"
          >
            Übernehmen
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Zuschneiden: Rahmen aufziehen, das Bild wird beschnitten. Rote Rahmen, Kreise,
          Pfeile und Buchstaben-Marker (A, B, C, …) sowie blaue Zahlen-Stempel (1–10)
          werden ins Bild übernommen — die Erfassungs-KI sieht deine Markierungen und du
          kannst dich im Dialog und im Text darauf beziehen („bei Zahl 3 …“). Schwärzungen
          werden dauerhaft deckend eingebrannt (Datenschutz). Die Lupe vergrößert nur die
          Ansicht.
        </p>
        <div className="min-h-0 flex-1 overflow-auto">
          <canvas
            ref={canvasRef}
            className={`max-w-full select-none ${werkzeug === "lupe" ? "cursor-zoom-in" : "cursor-crosshair"}`}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={() => {
              onMouseUp()
              setLupe(null)
            }}
          />
        </div>
      </div>
      {werkzeug === "lupe" && lupe && (
        <div
          className="pointer-events-none fixed z-[60] overflow-hidden rounded-full border-2 border-destructive shadow-2xl"
          style={{
            width: LUPE_D,
            height: LUPE_D,
            left: Math.min(lupe.cx + 20, window.innerWidth - LUPE_D - 8),
            top: Math.max(8, lupe.cy - LUPE_D - 20),
          }}
        >
          <canvas ref={lupeRef} width={LUPE_D} height={LUPE_D} className="block bg-card" />
        </div>
      )}
    </div>
  )
}
